export const AU_KM = 149597870.7;

interface PlanetRates {
  a: number;
  e: number;
  i: number;
  L: number;
  varpi: number;
  node: number;
}
export interface PlanetRow {
  id: string;
  name: string;
  a: number;
  e: number;
  i: number;
  L: number;
  varpi: number;
  node: number; // au, degrees at J2000
  rates: PlanetRates; // per Julian century
}

const row = (id: string, name: string, v: number[], r: number[]): PlanetRow => ({
  id,
  name,
  a: v[0],
  e: v[1],
  i: v[2],
  L: v[3],
  varpi: v[4],
  node: v[5],
  rates: { a: r[0], e: r[1], i: r[2], L: r[3], varpi: r[4], node: r[5] },
});

export const PLANETS: PlanetRow[] = [
  row(
    "mercury",
    "Mercury",
    [0.38709927, 0.20563593, 7.00497902, 252.2503235, 77.45779628, 48.33076593],
    [0.00000037, 0.00001906, -0.00594749, 149472.67411175, 0.16047689, -0.12534081],
  ),
  row(
    "venus",
    "Venus",
    [0.72333566, 0.00677672, 3.39467605, 181.9790995, 131.60246718, 76.67984255],
    [0.0000039, -0.00004107, -0.0007889, 58517.81538729, 0.00268329, -0.27769418],
  ),
  row(
    "earth",
    "Earth",
    [1.00000261, 0.01671123, -0.00001531, 100.46457166, 102.93768193, 0.0],
    [0.00000562, -0.00004392, -0.01294668, 35999.37244981, 0.32327364, 0.0],
  ),
  row(
    "mars",
    "Mars",
    [1.52371034, 0.0933941, 1.84969142, -4.55343205, -23.94362959, 49.55953891],
    [0.00001847, 0.00007882, -0.00813131, 19140.30268499, 0.44441088, -0.29257343],
  ),
  row(
    "jupiter",
    "Jupiter",
    [5.202887, 0.04838624, 1.30439695, 34.39644051, 14.72847983, 100.47390909],
    [-0.00011607, -0.00013253, -0.00183714, 3034.74612775, 0.21252668, 0.20469106],
  ),
  row(
    "saturn",
    "Saturn",
    [9.53667594, 0.05386179, 2.48599187, 49.95424423, 92.59887831, 113.66242448],
    [-0.0012506, -0.00050991, 0.00193609, 1222.49362201, -0.41897216, -0.28867794],
  ),
  row(
    "uranus",
    "Uranus",
    [19.18916464, 0.04725744, 0.77263783, 313.23810451, 170.9542763, 74.01692503],
    [-0.00196176, -0.00004397, -0.00242939, 428.48202785, 0.40805281, 0.04240589],
  ),
  row(
    "neptune",
    "Neptune",
    [30.06992276, 0.00859048, 1.77004347, -55.12002969, 44.96476227, 131.78422574],
    [0.00026291, 0.00005105, 0.00035372, 218.45945325, -0.32241464, -0.00508664],
  ),
];

export interface PhysicalRow {
  radiusKm: number;
  rotationPeriodHours: number;
  axialTiltDeg: number;
  /** km³/s² */
  gm: number;
  densityGcm3: number;
  meanTempC: number;
}

export const SUN_PHYSICAL: PhysicalRow = {
  radiusKm: 695700,
  rotationPeriodHours: 609.12,
  axialTiltDeg: 7.25,
  gm: 132712e6,
  densityGcm3: 1.408,
  meanTempC: 5499, // effective temperature 5772 K
};

// equatorial radii from the NASA planetary fact sheets, at the 1 bar level for the giants;
// GM, mean density and mean temperature (1 bar for the giants) from the same sheets
export const PLANET_PHYSICAL: Record<string, PhysicalRow> = {
  mercury: {
    radiusKm: 2440.5,
    rotationPeriodHours: 1407.6,
    axialTiltDeg: 0.034,
    gm: 22032,
    densityGcm3: 5.429,
    meanTempC: 167,
  },
  venus: {
    radiusKm: 6051.8,
    rotationPeriodHours: -5832.5,
    axialTiltDeg: 2.64,
    gm: 324860,
    densityGcm3: 5.243,
    meanTempC: 464,
  },
  earth: {
    radiusKm: 6378.137,
    rotationPeriodHours: 23.9345,
    axialTiltDeg: 23.44,
    gm: 398600,
    densityGcm3: 5.513,
    meanTempC: 15,
  },
  mars: {
    radiusKm: 3396.2,
    rotationPeriodHours: 24.6229,
    axialTiltDeg: 25.19,
    gm: 42828,
    densityGcm3: 3.934,
    meanTempC: -65,
  },
  jupiter: {
    radiusKm: 71492,
    rotationPeriodHours: 9.925,
    axialTiltDeg: 3.13,
    gm: 126687000,
    densityGcm3: 1.326,
    meanTempC: -110,
  },
  saturn: {
    radiusKm: 60268,
    rotationPeriodHours: 10.656,
    axialTiltDeg: 26.73,
    gm: 37931000,
    densityGcm3: 0.687,
    meanTempC: -140,
  },
  uranus: {
    radiusKm: 25559,
    rotationPeriodHours: -17.24,
    axialTiltDeg: 82.23,
    gm: 5794000,
    densityGcm3: 1.27,
    meanTempC: -195,
  },
  neptune: {
    radiusKm: 24764,
    rotationPeriodHours: 16.11,
    axialTiltDeg: 28.32,
    gm: 6835100,
    densityGcm3: 1.638,
    meanTempC: -200,
  },
};
